/**
 * Locale-specific public URL segments.
 * Logical (internal) paths always use English segments — e.g. `/solutions/foo`.
 * Turkish public paths use translated segments — e.g. `/tr/cozumler/foo`.
 */
export const ROUTE_SEGMENT_I18N = {
  services: { en: 'services', tr: 'hizmetler' },
  solutions: { en: 'solutions', tr: 'cozumler' },
  projects: { en: 'projects', tr: 'projeler' },
  tools: { en: 'tools', tr: 'araclar' },
  playbooks: { en: 'playbooks', tr: 'playbooklar' },
  guides: { en: 'guides', tr: 'rehberler' },
  about: { en: 'about', tr: 'hakkimizda' },
  contact: { en: 'contact', tr: 'iletisim' },
  'ai-answers': { en: 'ai-answers', tr: 'ai-cevaplari' },
  privacy: { en: 'privacy', tr: 'gizlilik-politikasi' },
  terms: { en: 'terms', tr: 'kullanim-sartlari' },
  'cookie-policy': { en: 'cookie-policy', tr: 'cerez-politikasi' },
  blog: { en: 'blog', tr: 'blog' },
  linkedin: { en: 'linkedin', tr: 'linkedin' },
  authors: { en: 'authors', tr: 'yazarlar' },
  'kvkk-aydinlatma-metni': { en: 'kvkk-aydinlatma-metni', tr: 'kvkk-aydinlatma-metni' },
  'delivery-cancellation-refund': { en: 'delivery-cancellation-refund', tr: 'teslim-iptal-iade' },
  'distance-sales-agreement': { en: 'distance-sales-agreement', tr: 'mesafeli-satis-sozlesmesi' },
  tesekkurler: { en: 'tesekkurler', tr: 'tesekkurler' },
};

const TR_SEGMENT_TO_LOGICAL = Object.fromEntries(
  Object.entries(ROUTE_SEGMENT_I18N).map(([logical, segments]) => [segments.tr, logical]),
);

const LOGICAL_TO_TR_SEGMENT = Object.fromEntries(
  Object.entries(ROUTE_SEGMENT_I18N).map(([logical, segments]) => [logical, segments.tr]),
);

function stripPathQueryHash(pathname) {
  return (pathname || '/').split('?')[0].split('#')[0] || '/';
}

function splitSegments(pathname) {
  const base = stripPathQueryHash(pathname);
  if (base === '/') return [];
  return base.split('/').filter(Boolean);
}

function joinSegments(segments) {
  if (!segments.length) return '/';
  return `/${segments.join('/')}`;
}

/** Normalize any public URL to the English logical path used in catalogs and SEO keys. */
export function toLogicalPath(pathname) {
  let segments = splitSegments(pathname);
  if (segments[0] === 'tr') segments = segments.slice(1);
  if (!segments.length) return '/';

  if (segments[0] === 'blog' && segments[1] === 'etiket') {
    segments[1] = 'tag';
  }

  const logicalKey = TR_SEGMENT_TO_LOGICAL[segments[0]] ?? segments[0];
  segments[0] = ROUTE_SEGMENT_I18N[logicalKey]?.en ?? logicalKey;
  return joinSegments(segments);
}

/** Build locale-specific public pathname from a logical path. */
export function toPublicPath(logicalPathname, locale, defaultLocale = 'en') {
  const logicalSegments = splitSegments(toLogicalPath(logicalPathname));

  if (locale === defaultLocale) {
    return joinSegments(logicalSegments);
  }

  const localized = logicalSegments.map((segment, index) => {
    if (index === 0) {
      const logicalKey = TR_SEGMENT_TO_LOGICAL[segment] ?? segment;
      return LOGICAL_TO_TR_SEGMENT[logicalKey] ?? segment;
    }
    if (index === 1 && logicalSegments[0] === 'blog' && segment === 'tag') {
      return 'etiket';
    }
    return segment;
  });

  return joinSegments(['tr', ...localized]);
}

/** First-segment logical keys that have distinct TR slugs (for redirects). */
export function getLocalizedRouteRoots() {
  return Object.entries(ROUTE_SEGMENT_I18N)
    .filter(([, segments]) => segments.en !== segments.tr)
    .map(([logical]) => logical);
}

/** There is no authors index page: every index URL variant 301s straight to the blog index. */
export const AUTHORS_INDEX_TARGET_BY_LOCALE = { en: '/blog', tr: '/tr/blog' };

export const AUTHORS_INDEX_REDIRECT_PAIRS = [
  ['/authors', 'en'],
  ['/authors/', 'en'],
  ['/en/authors', 'en'],
  ['/en/authors/', 'en'],
  ['/tr/yazarlar', 'tr'],
  ['/tr/yazarlar/', 'tr'],
  ['/tr/authors', 'tr'],
  ['/tr/authors/', 'tr'],
].map(([source, locale]) => ({ source, target: AUTHORS_INDEX_TARGET_BY_LOCALE[locale] }));

/** Public path for a logical path, except the authors index which resolves to the blog index. */
export function toLocaleRedirectTarget(logicalPathname, locale) {
  if (toLogicalPath(logicalPathname) === '/authors') return AUTHORS_INDEX_TARGET_BY_LOCALE[locale];
  return toPublicPath(logicalPathname, locale);
}

const LOCALE_QUERY_PATTERNS = [
  '/',
  '/about',
  '/linkedin',
  '/ai-answers',
  '/ai-answers/:slug',
  '/guides',
  '/guides/:slug',
  '/contact',
  '/services',
  '/services/:slug',
  '/solutions',
  '/solutions/:slug',
  '/playbooks',
  '/playbooks/:slug',
  '/projects',
  '/projects/:slug',
  '/blog',
  '/blog/:slug',
  '/blog/tag/:tagSlug',
  '/blog/etiket/:tagSlug',
  '/tools',
  '/tools/:slug',
  '/authors',
  '/authors/:slug',
  '/privacy',
  '/kvkk-aydinlatma-metni',
  '/terms',
  '/cookie-policy',
  '/delivery-cancellation-refund',
  '/distance-sales-agreement',
];

function padRedirectColumns(source, query, target) {
  return `${source.padEnd(22)} ${query.padEnd(11)} ${target.padEnd(28)} 301`;
}

/** Legacy ?lang= query → locale-specific public paths. */
export function buildLocaleQueryRedirectLines() {
  const lines = [];
  const trSources = new Set();
  for (const pattern of LOCALE_QUERY_PATTERNS) {
    const enTarget = toLocaleRedirectTarget(pattern, 'en');
    const trTarget = toLocaleRedirectTarget(pattern, 'tr');
    const trSource = toPublicPath(pattern, 'tr');
    lines.push(padRedirectColumns(pattern, 'lang=en', enTarget));
    lines.push(padRedirectColumns(pattern, 'lang=tr', trTarget));
    if (trSources.has(trSource)) continue;
    trSources.add(trSource);
    lines.push(padRedirectColumns(trSource, 'lang=en', enTarget));
  }
  lines.push('/en                   /                           301');
  lines.push('/en/:slug             /:slug                      301');
  return lines;
}

/** Redirect old /tr/{english-segment} URLs to /tr/{turkish-segment}. */
export function buildTrEnglishSegmentRedirectLines() {
  const lines = [];
  for (const root of getLocalizedRouteRoots()) {
    const enSeg = ROUTE_SEGMENT_I18N[root].en;
    const trSeg = ROUTE_SEGMENT_I18N[root].tr;
    lines.push(`/tr/${enSeg}              /tr/${trSeg}              301`);
    lines.push(`/tr/${enSeg}/             /tr/${trSeg}              301`);
    lines.push(`/tr/${enSeg}/:slug        /tr/${trSeg}/:slug        301`);
    lines.push(`/tr/${enSeg}/:slug/       /tr/${trSeg}/:slug        301`);
  }
  return lines;
}

/** Trailing-slash normalization for Turkish public paths. */
export function buildTrPublicTrailingSlashRedirectLines() {
  const lines = ['/tr/                  /tr                            301'];
  for (const root of getLocalizedRouteRoots()) {
    const trSeg = ROUTE_SEGMENT_I18N[root].tr;
    lines.push(`/tr/${trSeg}/              /tr/${trSeg}                      301`);
    lines.push(`/tr/${trSeg}/:slug/        /tr/${trSeg}/:slug                301`);
  }
  for (const root of ['blog', 'linkedin', 'kvkk-aydinlatma-metni']) {
    const seg = ROUTE_SEGMENT_I18N[root].tr;
    if (getLocalizedRouteRoots().includes(root)) continue;
    lines.push(`/tr/${seg}/              /tr/${seg}                      301`);
    if (root !== 'kvkk-aydinlatma-metni') {
      lines.push(`/tr/${seg}/:slug/        /tr/${seg}/:slug                301`);
    }
  }
  return lines;
}
